import { Feature } from '@/pinia/feature'

const s2l = (string: string) => ({
  en: string,
  sv: string
})

const classColorScale = {
  type: 'STEP',
  cutoffs: [
    { value: 0, color: '#40903a', label: s2l('A') },
    { value: 1, color: '#6da930', label: s2l('B') },
    { value: 2, color: '#cad228', label: s2l('C') },
    { value: 3, color: '#fbee30', label: s2l('D') },
    { value: 4, color: '#f1be26', label: s2l('E') },
    { value: 5, color: '#da711c', label: s2l('F') },
    { value: 6, color: '#d02920', label: s2l('G') }
  ]
}

const performanceColorScale = {
  type: 'INTERPOLATE',
  cutoffs: [
    { value: 0, color: '#40903a' },
    { value: 100, color: '#6da930' },
    { value: 150, color: '#cad228' },
    { value: 200, color: '#fbee30' },
    { value: 250, color: '#f1be26' },
    { value: 300, color: '#da711c' },
    { value: 350, color: '#d02920' }
  ]
}

const endDate = Date.now() / (24 * 60 * 60 * 1000)
const startDate = endDate - 365 * 10

const dateColorScale = {
  type: 'INTERPOLATE',
  cutoffs: [
    { value: startDate, color: '#d02920' },
    { value: (startDate + endDate) / 2, color: '#cad228' },
    { value: endDate, color: '#40903a' }
  ]
}

const yesNoColorScale = {
  type: 'STEP',
  cutoffs: [
    {
      value: 0,
      color: '#d02920',
      label: {
        en: 'No',
        sv: 'Nej'
      }
    },
    {
      value: 1,
      color: '#40903a',
      label: {
        en: 'Yes',
        sv: 'Ja'
      }
    }
  ]
}

export const energy: Feature = {
  name: 'report',
  enabled: true,
  isRoute: true,
  access: ['FEAT_ENERGY-ANALYSIS-REPORT_READER'],
  title: {
    sv: 'Energideklaration',
    en: 'Energy declaration'
  },
  component: 'DashboardDataSectionFlat',
  props: {
    enabledEntityLevels: ['REGION', 'COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION'],
    dataSources: [
      {
        id: 'energy.measurement.date.value',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        path: 'measurements[0].splits[0].value'
      },
      {
        id: 'energy.measurement.class.value',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        path: 'measurements[1].splits[0].value'
      },
      {
        id: 'energy.measurement.performance.value',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        path: 'measurements[2].splits[0].value'
      },
      {
        id: 'energy.measurement.specific-energy.value',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        path: 'measurements[3].splits[0].value'
      },
      {
        id: 'energy.measurement.radon.value',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        path: 'measurements[4].splits[0].value'
      },
      {
        id: 'energy.measurement.ventilation.value',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        path: 'measurements[5].splits[0].value'
      },
      {
        id: 'energy.measurement.date.colored-entities',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 6
        }
      },
      {
        id: 'energy.measurement.class.colored-entities',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 7
        }
      },
      {
        id: 'energy.measurement.performance.colored-entities',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 8
        }
      },
      {
        id: 'energy.measurement.specific-energy.colored-entities',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 9
        }
      },
      {
        id: 'energy.measurement.radon.colored-entities',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 10
        }
      },
      {
        id: 'energy.measurement.ventilation.colored-entities',
        type: 'fetch',
        fetch: 'fetch.energy.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 11
        }
      }
    ]
  },
  children: [
    {
      name: 'date',
      title: {
        sv: 'Utförandedatum',
        en: 'Execution date'
      },
      info: {
        en: 'The execution date is the date when the energy declaration was made. The energy declaration is valid for ten years from this date. The date shown when aggregated is the oldest date in the group, and the newest date is shown for each building.',
        sv: 'Utförandedatum är det datum då energideklarationen gjordes. Energideklarationen är giltig i tio år från detta datum. Datumet som visas vid aggregering är det äldsta datumet i gruppen, och det nyaste datumet visas för varje byggnad. '
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: false,
        value: {
          dataSource: 'energy.measurement.date.value',
          format: {
            format: {
              type: 'epoch-day'
            }
          },
          isLoading: false
        },
        coloredEntities: {
          dataSource: 'energy.measurement.date.colored-entities'
        },
        colorScale: dateColorScale,
        // color: '#000',
        colorLegend: {
          hideMean: true,
          labelStart: {
            sv: '10 år+',
            en: '10 years+'
          },
          labelEnd: {
            sv: 'I år',
            en: 'This year'
          }
        }
      }
    },
    {
      name: 'class',
      title: {
        sv: 'Energiklass',
        en: 'Energy class'
      },
      info: {
        en: "The energy classes A-G are based on the energy performance requirements for new buildings in Sweden. Class C represents the standard requirements for a new building. Energy classes are determined based on how a building's energy performance compares to these requirements.",
        sv: 'Energiklasserna A-G är baserade på energiprestandakraven för nya byggnader i Sverige. Klass C representerar standardkraven för en ny byggnad. Energiklasserna bestäms utifrån hur en byggnads energiprestanda jämförs med dessa krav.'
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: true,
        value: {
          dataSource: 'energy.measurement.class.value',
          format: {
            format: {
              type: 'number-floor'
            },
            steps: [
              {
                format: {
                  type: 'mapping',
                  mapping: {
                    0: 'A',
                    1: 'B',
                    2: 'C',
                    3: 'D',
                    4: 'E',
                    5: 'F',
                    6: 'G'
                  }
                }
              }
            ]
          }
        },
        coloredEntities: {
          dataSource: 'energy.measurement.class.colored-entities'
        },
        colorLegend: {
          hideMean: true,
          height: 42
        },
        colorScale: classColorScale
      }
    },
    {
      name: 'performance',
      title: {
        sv: 'Prestanda',
        en: 'Performance'
      },
      info: {
        en: "Energy performance is calculated by multiplying a building's energy use by weighting factors based on the type of energy carriers used. This gives a primary energy number, which is then divided by a geographic adjustment factor to make comparisons between buildings in different locations. The final result is expressed per square meter, allowing for comparisons between buildings of varying sizes.",
        sv: 'Energiprestanda beräknas genom att man multiplicerar byggnadens energianvändning med viktfaktorer baserade på vilka energibärare som används. Detta ger ett primärenergital som sedan divideras med en geografisk anpassningsfaktor för att möjliggöra jämförelser mellan byggnader på olika platser. Det slutgiltiga resultatet uttrycks per kvadratmeter och möjliggör jämförelser mellan byggnader av olika storlek.'
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: false,
        value: {
          dataSource: 'energy.measurement.performance.value',
          format: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 0
            },
            steps: [{ format: { type: 'format-string', format: '%s kWh/m²' } }]
          },
          isLoading: false
        },
        coloredEntities: {
          dataSource: 'energy.measurement.performance.colored-entities'
        },
        colorScale: performanceColorScale,
        colorLegend: {
          hideMean: true
        }
      }
    },
    {
      name: 'specific-energy',
      title: {
        sv: 'Specifik energiförbrukning',
        en: 'Specific energy consumption'
      },
      info: {
        sv: 'Den specifika energianvändningen får man genom att dividera byggnadens oviktade energianvändning med uppvärmda arean.',
        en: "The specific energy consumption is obtained by dividing the building's unweighted energy use by the heated area."
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: false,
        value: {
          dataSource: 'energy.measurement.specific-energy.value',
          format: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 0
            },
            steps: [{ format: { type: 'format-string', format: '%s kWh/m²' } }]
          },
          isLoading: false
        },
        coloredEntities: {
          dataSource: 'energy.measurement.specific-energy.colored-entities'
        },
        colorScale: performanceColorScale,
        colorLegend: {
          hideMean: true
        }
      }
    },
    {
      name: 'radon',
      title: {
        sv: 'Radonmätning',
        en: 'Radon measurement'
      },
      info: {
        en: 'Radon is a radioactive gas that can accumulate in buildings and pose a health risk. This measurement indicates whether a radon measurement has been performed for a building and how many aggregated over the group.',
        sv: 'Radon är en radioaktiv gas som kan ansamlas i byggnader och utgöra en hälsorisk. Denna mätning indikerar om en radonmätning har utförts för en byggnad och hur många som har utförts aggregerat över gruppen.'
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: false,
        value: {
          dataSource: 'energy.measurement.radon.value',
          format: {
            format: {
              type: 'locale-mapping',
              mapping: {
                0: {
                  en: 'No',
                  sv: 'Nej'
                },
                1: {
                  en: 'Yes',
                  sv: 'Ja'
                }
              }
            }
          },
          isLoading: false
        },
        coloredEntities: {
          dataSource: 'energy.measurement.radon.colored-entities'
        },
        colorScale: yesNoColorScale,
        colorLegend: {
          hideMean: true,
          width: 100,
          height: 42
        }
      }
    },
    {
      name: 'ventilation',
      title: {
        sv: 'Ventilationskontroll',
        en: 'Ventilation control'
      },
      info: {
        en: "Ventilation control is a measure of how well a building's ventilation system is functioning. This measurement indicates whether a ventilation control has been performed for a building and how many aggregated over the group.",
        sv: 'Ventilationskontroll är ett mått på hur väl en byggnads ventilationssystem fungerar. Denna mätning indikerar om en ventilationskontroll har utförts för en byggnad och hur många som utförts aggregerat över gruppen.'
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: false,
        value: {
          dataSource: 'energy.measurement.ventilation.value',
          format: {
            format: {
              type: 'locale-mapping',
              mapping: {
                0: {
                  en: 'No',
                  sv: 'Nej'
                },
                1: {
                  en: 'Yes',
                  sv: 'Ja'
                }
              }
            }
          },
          isLoading: false
        },
        coloredEntities: {
          dataSource: 'energy.measurement.ventilation.colored-entities'
        },
        colorScale: yesNoColorScale,
        colorLegend: {
          hideMean: true,
          width: 100,
          height: 42
        }
      }
    }
  ]
}
